import type { Message } from '@/ai/utils/types';

/**
 * 챗봇 generate_report 도구가 생성하는 ReportPayload 의 frontend 타입 정의 + 파서.
 * Backend ReportPayloadValidator 와 1:1 대응. ChatMessage.result 의 JSON 문자열을
 * 파싱해 ReportPayload 로 좁힌다 — type 미스매치 / 미지원 section type 시 null.
 *
 * 주의: 차트 series.data / table.rows 의 수치는 LLM 이 직접 작성한 값이므로
 *       Backend 가 검증한 type 만 신뢰 (구체 값은 hallucination 가능성 인지).
 */

export const REPORT_SECTION_TYPES = ['kpi', 'chart', 'table', 'text', 'stats', 'evidence'] as const;
export type ReportSectionType = (typeof REPORT_SECTION_TYPES)[number];

export const CHART_TYPES = ['bar', 'line', 'pie', 'donut'] as const;
export type ReportChartType = (typeof CHART_TYPES)[number];

export const VERDICT_LEVELS = ['ok', 'warning', 'critical', 'info'] as const;
export type VerdictLevel = (typeof VERDICT_LEVELS)[number];

export interface ReportVerdict {
  level: VerdictLevel;
  label: string;
}

export interface KpiItem {
  label: string;
  value: string | number;
  unit?: string;
  level?: VerdictLevel;
  delta?: { value: number; unit?: string; direction?: 'up' | 'down' | 'flat' };
}

export interface KpiSection {
  type: 'kpi';
  title?: string;
  items: KpiItem[];
}

export interface ChartSeries {
  name: string;
  /**
   * line/bar: number[] (xAxis.categories 에 정렬) 또는 [string|number, number][] 튜플.
   * pie/donut: { name, value }[] — 단일 series.
   */
  data: Array<number | string | [string | number, number] | { name: string; value: number }>;
  color?: string;
}

export interface ChartMarker {
  /** 누락 시 렌더러가 좌표 필드로 추론 (x+y→point, x→vline, y→hline, from/to→band). */
  kind?: 'point' | 'vline' | 'hline' | 'band';
  /** point/vline: xAxis 카테고리 값 */
  x?: string | number;
  /** point(좌표)/hline(임계선) */
  y?: number;
  /** band 시작 (xAxis 카테고리) */
  from?: string | number;
  /** band 끝 */
  to?: string | number;
  label: string;
  /** 기본 info */
  level?: 'critical' | 'warning' | 'info';
}

export interface ChartSection {
  type: 'chart';
  chartType: ReportChartType;
  title: string;
  subtitle?: string;
  xAxis?: { name?: string; categories?: string[] };
  yAxis?: { name?: string; unit?: string };
  series: ChartSeries[];
  /** line/bar 전용 — pie/donut 은 무시. */
  markers?: ChartMarker[];
}

export interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  format?: 'number' | 'datetime' | 'percent';
}

export interface TableSection {
  type: 'table';
  title: string;
  columns: TableColumn[];
  rows: Array<Record<string, string | number | null>>;
  rowLink?: { kind: string; idKey: string };
}

export interface TextSection {
  type: 'text';
  title?: string;
  body: string;
  style?: 'info' | 'warning';
}

export interface StatsItem {
  label: string;
  value: string;
}

export interface StatsSection {
  type: 'stats';
  title?: string;
  items: StatsItem[];
}

export interface EvidenceItem {
  claim: string;
  source_tool: string;
  metric?: string;
  value?: string;
  at?: string;
  /** markdown 링크 — 기존 봇메시지의 target:// 링크 형식 */
  link_md?: string;
  weight?: 'primary' | 'supporting';
}

export interface EvidenceSection {
  type: 'evidence';
  title?: string;
  origin?: { name: string; statement: string; link_md?: string };
  items: EvidenceItem[];
}

export type ReportSection =
  | KpiSection
  | ChartSection
  | TableSection
  | TextSection
  | StatsSection
  | EvidenceSection;

export interface ReportPayload {
  schemaVersion: number;
  reportId: string;
  title: string;
  subtitle?: string;
  topic?: string;
  generatedAt: string;
  generatedBy: { tool: string; basedOnTools?: string[] };
  summaryText: string;
  verdict?: ReportVerdict;
  sections: ReportSection[];
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isReportSection = (v: unknown): v is ReportSection => {
  if (!isObject(v)) return false;
  const t = v.type;
  return typeof t === 'string' && (REPORT_SECTION_TYPES as readonly string[]).includes(t);
};

/**
 * Message.result 의 JSON 문자열을 ReportPayload 로 파싱.
 * 실패(파싱 오류 / 필수 필드 누락 / 알 수 없는 section type) 시 null — 호출 측에서 graceful fallback.
 */
export const parseReportPayload = (raw: string | null | undefined): ReportPayload | null => {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isObject(parsed)) return null;
  const reportId = parsed.reportId;
  const title = parsed.title;
  const summaryText = parsed.summaryText;
  const generatedAt = parsed.generatedAt;
  const sections = parsed.sections;
  if (
    typeof reportId !== 'string' ||
    typeof title !== 'string' ||
    typeof summaryText !== 'string' ||
    typeof generatedAt !== 'string' ||
    !Array.isArray(sections) ||
    sections.length === 0
  ) {
    return null;
  }
  // 모든 section 의 type 이 알려진 값인지 확인 — 하나라도 unknown 이면 전체 null (silent skip 보다 명시적 실패).
  if (!sections.every(isReportSection)) return null;

  const generatedBy = isObject(parsed.generatedBy)
    ? parsed.generatedBy
    : { tool: 'generate_report' };

  return {
    schemaVersion: typeof parsed.schemaVersion === 'number' ? parsed.schemaVersion : 1,
    reportId,
    title,
    subtitle: typeof parsed.subtitle === 'string' ? parsed.subtitle : undefined,
    topic: typeof parsed.topic === 'string' ? parsed.topic : undefined,
    generatedAt,
    generatedBy: {
      tool: typeof generatedBy.tool === 'string' ? generatedBy.tool : 'generate_report',
      basedOnTools: Array.isArray(generatedBy.basedOnTools)
        ? (generatedBy.basedOnTools as string[]).filter((s) => typeof s === 'string')
        : undefined,
    },
    summaryText,
    verdict:
      isObject(parsed.verdict) &&
      typeof parsed.verdict.level === 'string' &&
      (VERDICT_LEVELS as readonly string[]).includes(parsed.verdict.level)
        ? {
            level: parsed.verdict.level as VerdictLevel,
            label:
              typeof parsed.verdict.label === 'string'
                ? parsed.verdict.label
                : parsed.verdict.level,
          }
        : undefined,
    sections,
  };
};

/**
 * chat history 의 메시지 배열에서 resultType==='report' 항목만 ReportPayload 로 파싱하여
 * 시간 내림차순으로 반환. ReportListView 가 이 배열로 list 카드를 렌더.
 */
export const extractReportsFromMessages = (messages: Message[]): ReportPayload[] => {
  return messages
    .filter((m) => m.sender === 'chatbot' && m.resultType === 'report')
    .map((m) => parseReportPayload(m.result))
    .filter((r): r is ReportPayload => r !== null)
    .sort((a, b) => (a.generatedAt < b.generatedAt ? 1 : -1));
};

/** 하나의 메시지 → ReportPayload (Detail view 용 단일 lookup). */
export const findReportInMessages = (
  messages: Message[],
  reportId: string,
): ReportPayload | null => {
  for (const m of messages) {
    if (m.sender !== 'chatbot' || m.resultType !== 'report') continue;
    const p = parseReportPayload(m.result);
    if (p && p.reportId === reportId) return p;
  }
  return null;
};
